import { ProgramRevision, ProgramRevisionStatus } from "@humanex/interfaces";
import { Box, TiptapView } from "@humanex/ui/components";
import { cn, useI18n } from "@humanex/ui/libs";
import { ChevronDown } from "lucide-react";
import { useState } from "react";

import { useProgramRevisions } from "@/hooks/queries";

const PROGRAM_TYPE_LABELS: Record<string, string> = {
  DU_HOC: "Du học",
  VIEC_LAM: "Việc làm",
  XUAT_KHAU: "Xuất khẩu lao động",
  HOC_NGHE: "Học nghề"
};

interface RevisionsPageProps {
  orgId: string;
  programId: string;
}

const REVISION_STATUS_CONFIG: Record<string, { label: string; color: string }> = {
  DRAFT: { label: "Nháp", color: "bg-gray-100 text-gray-600" },
  PENDING_REVIEW: {
    label: "Chờ duyệt",
    color: "bg-warning-100 text-warning-700"
  },
  APPROVED: {
    label: "Đã phê duyệt",
    color: "bg-success-100 text-success-700"
  },
  REJECTED: { label: "Bị từ chối", color: "bg-error-100 text-error-700" },
  SUPERSEDED: { label: "Đã thay thế", color: "bg-gray-100 text-gray-400" }
};

export function RevisionsPage({ programId }: RevisionsPageProps) {
  const { data: revisions, isLoading } = useProgramRevisions(programId);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <p className="text-responsive-xl font-normal text-text-primary">Lịch sử xét duyệt</p>
      </div>

      {isLoading ? (
        <div className="space-y-4">
          {[1, 2].map((i) => (
            <Box key={i} className="animate-pulse space-y-3">
              <div className="h-4 bg-gray-200 rounded w-1/4" />
              <div className="h-3 bg-gray-200 rounded w-1/2" />
              <div className="h-3 bg-gray-200 rounded w-3/4" />
            </Box>
          ))}
        </div>
      ) : !revisions || revisions.length === 0 ? (
        <Box>
          <p className="text-text-secondary text-sm text-center py-4">Chưa có lịch sử xét duyệt</p>
        </Box>
      ) : (
        <div className="space-y-4">
          {revisions.map((revision, i) => (
            <RevisionCard key={revision.id} revision={revision} defaultExpanded={i === 0} />
          ))}
        </div>
      )}
    </div>
  );
}

function RevisionCard({
  revision,
  defaultExpanded = false
}: {
  revision: ProgramRevision;
  defaultExpanded?: boolean;
}) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const { formatDate } = useI18n();

  const statusCfg = REVISION_STATUS_CONFIG[revision.status] ?? {
    label: revision.status,
    color: "bg-gray-100 text-gray-600"
  };

  return (
    <Box className="space-y-4">
      <button
        type="button"
        onClick={() => setExpanded((prev) => !prev)}
        className="flex items-center gap-3 flex-wrap w-full text-left"
      >
        <span className="text-responsive-md font-medium text-text-primary">Phiên bản {revision.version}</span>
        <span
          className={`inline-block px-2 py-0.5 rounded-full text-responsive-sm font-medium ${statusCfg.color}`}
        >
          {statusCfg.label}
        </span>
        <span className="text-responsive-base text-text-secondary ml-auto">
          {formatDate(revision.createdAt)}
        </span>
        <ChevronDown
          className={cn(
            "w-4 h-4 text-text-secondary transition-transform duration-200",
            expanded && "rotate-180"
          )}
        />
      </button>

      <div className="space-y-3 text-sm">
        {revision.submittedAt && (
          <TimelineRow
            label="Lần cuối nộp xét duyệt"
            timestamp={formatDate(revision.submittedAt)}
            note={revision.submitNote}
            color="text-secondary-600"
          />
        )}

        {revision.status === ProgramRevisionStatus.REJECTED && revision.reviewedAt && (
          <TimelineRow
            label="Bị từ chối"
            timestamp={formatDate(revision.reviewedAt)}
            note={revision.reviewNote}
            color="text-error-600"
            noteColor="bg-error-50 border-error-200 text-error-800"
          />
        )}

        {revision.status !== ProgramRevisionStatus.REJECTED && revision.reviewedAt && (
          <TimelineRow
            label="Đã phê duyệt"
            timestamp={formatDate(revision.reviewedAt)}
            note={revision.reviewNote}
            color="text-success-600"
          />
        )}

        {revision.publishedAt && (
          <TimelineRow
            label="Đã xuất bản"
            timestamp={formatDate(revision.publishedAt)}
            color="text-success-700"
          />
        )}
      </div>
      {expanded && (
        <div className="border-t border-gray-100 pt-4">
          <RevisionContent revision={revision} />
        </div>
      )}
    </Box>
  );
}

function RevisionContent({ revision }: { revision: ProgramRevision }) {
  const { formatDate } = useI18n();

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      <div className="lg:col-span-2 space-y-4">
        <div className="space-y-1">
          <p className="text-xs font-medium text-text-secondary uppercase tracking-wide">Tiêu đề</p>
          <p className="text-responsive-base text-text-primary font-medium">{revision.title}</p>
        </div>

        <div className="space-y-1">
          <p className="text-xs font-medium text-text-secondary uppercase tracking-wide">Mô tả</p>
          <TiptapView content={revision.description} variant="compact" />
        </div>

        {revision.requirements && (
          <div className="space-y-1">
            <p className="text-xs font-medium text-text-secondary uppercase tracking-wide">
              Yêu cầu ứng viên
            </p>
            <TiptapView content={revision.requirements} variant="compact" />
          </div>
        )}

        {revision.benefits && (
          <div className="space-y-1">
            <p className="text-xs font-medium text-text-secondary uppercase tracking-wide">Quyền lợi</p>
            <TiptapView content={revision.benefits} variant="compact" />
          </div>
        )}
      </div>

      <div className="space-y-2 text-sm border border-gray-100 rounded-lg p-3 h-fit bg-gray-50">
        <p className="text-responsive-sm font-medium text-text-primary">Thông tin chi tiết</p>
        <RevisionDetailRow label="Loại" value={PROGRAM_TYPE_LABELS[revision.type] ?? revision.type} />
        <RevisionDetailRow label="Chỉ tiêu" value={String(revision.quota)} />
        {revision.country && <RevisionDetailRow label="Quốc gia" value={revision.country} />}
        {revision.location && <RevisionDetailRow label="Địa điểm" value={revision.location} />}
        <RevisionDetailRow
          label="Hạn nộp"
          value={
            revision.deadline
              ? formatDate(revision.deadline, {
                  month: "short",
                  day: "numeric",
                  year: "numeric"
                })
              : "Không giới hạn"
          }
        />
        <RevisionDetailRow label="Đặt cọc" value={`${revision.depositAmount.toLocaleString("vi-VN")} VNĐ`} />
        <RevisionDetailRow label="Chi phí" value={revision.costVisible ? "Công khai" : "Ẩn"} />
      </div>
    </div>
  );
}

function RevisionDetailRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-2">
      <span className="text-text-secondary shrink-0">{label}</span>
      <span className="text-text-primary font-medium text-right">{value}</span>
    </div>
  );
}

function TimelineRow({
  label,
  timestamp,
  note,
  color,
  noteColor = "bg-gray-50 border-gray-200 text-gray-700"
}: {
  label: string;
  timestamp: string | null;
  note?: string | null;
  color: string;
  noteColor?: string;
}) {
  return (
    <div className="space-y-1">
      <div className="flex items-center gap-2 text-responsive-base">
        <span className={cn("font-medium", color)}>{label}</span>
        {timestamp && <span className="text-text-secondary">{timestamp}</span>}
      </div>
      {note && <p className={`text-responsive-base px-3 py-2 rounded border ${noteColor}`}>{note}</p>}
    </div>
  );
}
